<template>
  <InsideContent :title="$t('ADVERTISEMENT.STATS.CAMPAIGN.TITLE')">
    <template #actions>
      <StrokeButton plain size="small" type="primary" @click="getCSV">
        {{ $t('EXPORT_CSV') }}
      </StrokeButton>

      <RangeFilter :value="range" @input="$emit('update:range', $event)" />
    </template>

    <div class="row discovery-stats-overall__rates">
      <div class="columns large-3 medium-6 small-12">
        <DiscoveryBoxRate
          v-loading="loading"
          :label="$t('ADVERTISEMENT.DISCOVERY_ADS.IMPACT_RATE_TO_INSTALL')"
          :value="stats.campaignRates.m2i"
          :average="stats.globalRates.m2i"
        />
      </div>

      <div class="columns large-3 medium-6 small-12">
        <DiscoveryBoxRate
          v-loading="loading"
          :label="$t('ADVERTISEMENT.DISCOVERY_ADS.IMPACT_RATE_TO_VISIT')"
          :value="stats.campaignRates.m2v"
          :average="stats.globalRates.m2v"
        />
      </div>

      <div class="columns large-3 medium-6 small-12">
        <DiscoveryBoxRate
          v-loading="loading"
          :label="$t('ADVERTISEMENT.DISCOVERY_ADS.VISIT_RATE_TO_DOWNLOAD')"
          :value="stats.campaignRates.v2d"
          :average="stats.globalRates.v2d"
        />
      </div>

      <div class="columns large-3 medium-6 small-12">
        <DiscoveryBoxRate
          v-loading="loading"
          :label="$t('ADVERTISEMENT.DISCOVERY_ADS.DOWNLOAD_RATE_TO_INSTALL')"
          :value="stats.campaignRates.d2i"
          :average="stats.globalRates.d2i"
        />
      </div>
    </div>

    <div class="row">
      <div class="columns large-7 small-12">
        <DiscoveryStatsChart v-loading="loading" :dates="stats.dates" />
      </div>

      <div class="columns large-5 small-12">
        <DiscoveryBoxBudget
          v-loading="loading"
          class="discovery-stats-overall__budget-box"
          :spent-budget="stats.totalSpent"
          :average-spent-budget="stats.averageDailySpent"
          :today-spent-budget="stats.todaySpent"
        />
        <DiscoveryBoxInstall
          v-loading="loading"
          :total-install="stats.totalInstalls"
          :average-daily-install="stats.averageDailyInstalls"
        />
      </div>
    </div>

    <DiscoveryStatsHints
      v-if="!loading"
      class="discovery-stats-overall__hints"
      :package-name="packageName"
      :show-v2d="stats.campaignRates.v2d < stats.globalRates.v2d"
      :show-m2v="stats.campaignRates.m2v < stats.globalRates.m2v"
      :show-d2i="stats.campaignRates.d2i < stats.globalRates.d2i"
    />
  </InsideContent>
</template>

<script>
import FileSaver from 'file-saver';
import InsideContent from '@@/common/layout/InsideContent';
import RangeFilter from '@@/common/RangeFilter';
import DiscoveryBoxInstall from './DiscoveryBoxInstall';
import DiscoveryBoxRate from './DiscoveryBoxRate';
import DiscoveryBoxBudget from './DiscoveryBoxBudget';
import DiscoveryStatsChart from './DiscoveryStatsChart';
import StrokeButton from '../../../common/StrokeButton';
import DiscoveryStatsHints from './DiscoveryStatsHints';

import { AbortedError } from '@/api/shared/transformers/errorTransformer';
import { requestQueue, DiscoveryAdsCampaigns } from '@/api/api';

export default {
  name: 'DiscoveryStatsOverall',

  components: {
    InsideContent,
    RangeFilter,
    DiscoveryBoxInstall,
    DiscoveryBoxRate,
    DiscoveryBoxBudget,
    DiscoveryStatsChart,
    StrokeButton,
    DiscoveryStatsHints,
  },

  props: {
    campaignId: {
      type: [String, Number],
      required: true,
    },
    packageName: {
      type: String,
      default: '',
    },
    range: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      stats: {
        totalSpent: 0,
        averageDailySpent: 0,
        todaySpent: 0,
        totalInstalls: 0,
        averageDailyInstalls: 0,
        dates: {},
        campaignRates: {
          m2i: 0,
          m2v: 0,
          v2d: 0,
          d2i: 0,
        },
        globalRates: {
          m2i: 0,
          m2v: 0,
          v2d: 0,
          d2i: 0,
        },
      },
    };
  },

  watch: {
    range() {
      this.updateData();
    },
  },

  mounted() {
    this.getCampaignStats();
  },

  methods: {
    getCampaignStats() {
      this.loading = true;

      return DiscoveryAdsCampaigns.getOverall(this.campaignId, this.range)
        .then(stats => {
          this.stats = stats;
        })
        .catch(error => {
          if (error instanceof AbortedError) return;
          const { message } = error;
          this.$message.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateData() {
      if (this.loading) {
        requestQueue.flush();
      }

      this.getCampaignStats();
    },
    getCSV() {
      return DiscoveryAdsCampaigns.saveOverallCSV(this.campaignId, this.range)
        .then(file => {
          FileSaver.saveAs(file, file.name);
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },
  },
};
</script>

<style lang="scss">
.discovery-stats-overall {
  &__rates {
    margin-bottom: $global-margin * 1.5;
  }

  &__budget-box {
    margin-bottom: $global-margin * 1.5;
  }

  &__hints {
    margin-top: $global-margin * 1.5;
  }
}
</style>
